import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import PatientDetailDropdownCard from '../../components/PatientDetailDropdownCard';
import useAxios from '../../services/AxiosConfig';
import { showPasswordResetModal } from '../../slicers/PasswordResetSlice';

const SecurityActivityModal = ({ show, handleClose }) => {
  const dispatch = useDispatch();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const axiosConfig = useSelector((state) => state.axiosConfig);
  const httpService = useAxios(axiosConfig.envURL);

  useEffect(() => {
    if (show) {
      // Reset state when modal opens
      setActivities([]);
      setLastEvaluatedKey(null);
      setHasMore(false);
      fetchSecurityActivity();
    }
  }, [show]);

  const fetchSecurityActivity = async (loadMore = false) => {
    if (loadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    
    try {
      const params = {
        limit: 20, // Items per page
      };
      
      if (loadMore && lastEvaluatedKey) {
        params.lastEvaluatedKey = JSON.stringify(lastEvaluatedKey);
      }
      
      const response = await httpService.get('/security-activity', { params });
      if (response.data && response.data.data) {
        const { items, lastEvaluatedKey: newLastEvaluatedKey, hasMore: moreAvailable } = response.data.data;
        
        if (loadMore) {
          setActivities(prev => [...prev, ...items]);
        } else {
          setActivities(items);
        }
        
        setLastEvaluatedKey(newLastEvaluatedKey);
        setHasMore(moreAvailable);
      }
    } catch (error) {
      console.error('Failed to fetch security activity:', error);
      if (!loadMore) {
        setActivities([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      fetchSecurityActivity(true);
    }
  };

  const handleAcknowledge = async (createdDate, currentAcknowledged) => {
    try {
      // Toggle: if acknowledged, set to false; if not acknowledged, set to true
      const newAcknowledged = !currentAcknowledged;
      await httpService.post('/security-activity/acknowledge', { 
        createdDate,
        acknowledged: newAcknowledged 
      });
      // Refresh the list from the beginning
      setActivities([]);
      setLastEvaluatedKey(null);
      setHasMore(false);
      await fetchSecurityActivity();
    } catch (error) {
      console.error('Failed to acknowledge security activity:', error);
    }
  };

  const handleSecureAccount = () => {
    // Redirect to password change flow
    handleClose();
    // Trigger password reset modal - this matches the existing pattern
    dispatch(showPasswordResetModal({
      type: 'change',
      message: '',
      daysSinceLastChange: 0,
      policyDays: 0,
      daysUntilExpiry: 0,
      urgency: 'warning',
      lastPasswordChange: null
    }));
  };

  const formatTimeAgo = (createdDate) => {
    if (!createdDate) return '';
    const date = new Date(createdDate);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    return `${diffDays}d`;
  };

  const getStatusBadge = (acknowledged) => {
    if (acknowledged) {
      return { name: 'Recognized', color: '#6B5B00' };
    }
    return { name: 'New', color: '#1F5C29' };
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Security Activity</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="caption" style={{ marginBottom: '16px' }}>
          Security activity and alerts from the last 28 days.
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>Loading...</div>
        ) : activities.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>No security activity found.</div>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', maxHeight: '480px', overflowY: 'auto' }}>
              {activities.map((item, index) => (
                <div key={`${item.accountId}-${item.createdDate}-${index}`} style={{ width: '100%' }}>
                  <PatientDetailDropdownCard
                    title={item.title}
                    notePreview={item.location ? `${item.location}, ${item.deviceType}` : item.deviceType || 'Unknown device'}
                    label={getStatusBadge(item.acknowledged)}
                    rightComponent={<span className="caption" style={{ whiteSpace: 'nowrap' }}>{formatTimeAgo(item.createdDate)}</span>}
                    cardInfo={
                      !item.acknowledged ? (
                        <SecurityActivityButtons
                          onYes={() => handleAcknowledge(item.createdDate, false)}
                          onSecureAccount={handleSecureAccount}
                        />
                      ) : (
                        <div style={{ padding: '8px 0' }}>
                          <button
                            onClick={() => handleAcknowledge(item.createdDate, true)}
                            style={{
                              padding: '8px 16px',
                              backgroundColor: '#1D5769',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontFamily: 'Roboto',
                              fontSize: '14px',
                              fontWeight: 500,
                            }}
                          >
                            Change reply
                          </button>
                        </div>
                      )
                    }
                    width="100%"
                  />
                </div>
              ))}
            </div>
            {hasMore && (
              <div style={{ textAlign: 'center', padding: '16px 0', borderTop: '1px solid #e0e0e0', marginTop: '16px' }}>
                <button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  style={{
                    padding: '10px 24px',
                    backgroundColor: loadingMore ? '#ccc' : '#1D5769',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: loadingMore ? 'not-allowed' : 'pointer',
                    fontFamily: 'Roboto',
                    fontSize: '14px',
                    fontWeight: 500,
                  }}
                >
                  {loadingMore ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

const SecurityActivityButtons = ({ onYes, onSecureAccount }) => {
  return (
    <div style={{ padding: '8px 0' }}>
      <div style={{ marginBottom: '12px', fontFamily: 'Roboto', fontSize: '14px', color: '#333' }}>
        Do you recognize this activity? If you do not, secure your account and contact the help desk.
      </div>
      <div style={{ display: 'flex', gap: '12px' }}>
        <button
          onClick={onYes}
          style={{
            padding: '8px 16px',
            backgroundColor: '#1D5769',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontFamily: 'Roboto',
            fontSize: '14px',
            fontWeight: 500,
            flex: 1,
          }}
        >
          Yes
        </button>
        <button
          onClick={onSecureAccount}
          style={{
            padding: '8px 16px',
            backgroundColor: 'white',
            color: '#1D5769',
            border: '1px solid #1D5769',
            borderRadius: '6px',
            cursor: 'pointer',
            fontFamily: 'Roboto',
            fontSize: '14px',
            fontWeight: 500,
            flex: 1,
          }}
        >
          Secure Account
        </button>
      </div>
    </div>
  );
};

export default SecurityActivityModal;
